/*
 * Copyright © 2025. Legalbird eine Marke der Legalbird GmbH
 */

import React, { useState } from "react";
import FormFader from "../../Common/Fader/FormFader";
import wizardStyle from "../../wizardStyle";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import { Box, Button, Link, Stack, Typography } from "@mui/material";
import FormLegend from "../FormLegend/FormLegend";
import { apiPost } from "../../../../services/apiClient";
import { useCustomer } from "../../../../provider/Customer/CustomerProvider";
import { getCustomerConsent } from "../../../../services/customerService";
import { convertToLocaleString } from "../../../../services/dateConverter";
import RecordingConsentPrivacyModal from "./RecordingConsentPrivacyModal";
import { gaEvent } from "../../../../services/googleAnalyticsService";

type RecordingConsentFormProps = {
  handleConsent: (state: string) => void;
};

export default function RecordingConsentForm({ handleConsent }: RecordingConsentFormProps) {
  const { customer, refreshCustomer } = useCustomer();
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  if (!customer) {
    return null;
  }

  const consent = getCustomerConsent(customer, "callRecordingAndSummary");

  const setConsent = async (state: string) => {
    setIsLoading(true);

    gaEvent({ category: "Callaufzeichnung", action: state === "approve" ? "Zugestimmt" : "Abgelehnt" });

    try {
      await apiPost(`/customer_consents/callRecordingAndSummary/${state}`, {});

      refreshCustomer();

      handleConsent(state);
    } catch (error) {
      setIsLoading(false);
    }
  };

  const openModal = () => {
    setIsPrivacyModalOpen(true);
  };

  return (
    <Paper sx={wizardStyle.paper} elevation={4}>
      <FormFader>
        <Box>
          <Grid
            container
            sx={{
              justifyContent: "center",
              alignItems: "baseline"
            }}>
            <Grid size={12}>
              <FormLegend>
                <strong>Unser Service für Sie: Persönliche Zusammenfassung</strong>
              </FormLegend>
            </Grid>
            <Grid size={12}>
              <Typography sx={{ textAlign: "center", marginTop: "2rem" }}>
                <strong>Sie erhalten bei uns vertraulich und kostenlos eine Zusammenfassung ihres Gesprächs.</strong> So müssen Sie nicht mitschreiben, sondern
                können sich voll und ganz auf das Gespräch konzentrieren. Wenn Sie dies möchten, können wir hierzu <u>alle zukünftigen Beratungsgespräche</u>{" "}
                bei uns aufzeichnen und fassen die Kernthemen, Absprachen und das weitere Vorgehen für Sie zusammen. So haben Sie alle Informationen jederzeit
                griffbereit!
              </Typography>
              <Typography sx={{ textAlign: "center", marginTop: "1rem" }}>
                <strong>
                  Weitere Informationen hierzu finden Sie unter{" "}
                  <Link onClick={() => openModal()} sx={{ textDecoration: "none", cursor: "pointer" }}>
                    Datenschutzhinweise zur Gesprächsaufzeichnung.
                  </Link>
                </strong>
              </Typography>
              {consent && (
                <Typography sx={{ textAlign: "center", marginTop: "1rem" }}>
                  <strong>Status: </strong>
                  {consent?.approvalDate && <>Zugestimmt am {convertToLocaleString(consent.approvalDate)}</>}
                  {consent?.denialDate && <>Abgelehnt am {convertToLocaleString(consent.denialDate)}</>}
                </Typography>
              )}
            </Grid>
            <Grid sx={{ my: 5 }} size={12}>
              <Stack spacing={2} sx={{ width: "100%", maxWidth: 420, mx: "auto" }}>
                <Button fullWidth variant="contained" color="primary" onClick={() => setConsent("approve")} disabled={isLoading}>
                  Zustimmen
                </Button>

                <Button fullWidth variant="outlined" color="primary" onClick={() => setConsent("deny")} disabled={isLoading}>
                  Ablehnen
                </Button>
              </Stack>
            </Grid>
            <Grid size={12}>
              <Typography sx={{ textAlign: "center" }}>
                Sie haben die Möglichkeit, Ihre Einwilligung <strong>jederzeit</strong> in Ihrer Servicewelt zu widerrufen.
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </FormFader>
      <RecordingConsentPrivacyModal open={isPrivacyModalOpen} onClose={() => setIsPrivacyModalOpen(false)} />
    </Paper>
  );
}
